import { useCallback } from 'react'
import useSWRInfinite from 'swr/infinite'
import {
  CreateLibraryCollectionBody,
  LibraryCollectionCard,
  LibraryCollectionCardsPage,
  LibraryCollectionDetail,
} from '@digital-twins/shared-types'

import { libraryCollectionsApi } from '@/lib/api'

export const LIBRARY_COLLECTIONS_LIST_SWR_KEY = 'library/collections'

/**
 * The list's page keys, `[name, cursor]` (null for the first page); null
 * after the last page. Exported so a write elsewhere refetches the list
 * through `unstable_serialize`.
 */
export function libraryCollectionsListKey(
  pageIndex: number,
  previous: LibraryCollectionCardsPage | null,
): [string, string | null] | null {
  if (pageIndex > 0 && !previous?.nextCursor) {
    return null
  }
  return [LIBRARY_COLLECTIONS_LIST_SWR_KEY, previous?.nextCursor ?? null]
}

export interface UseLibraryCollectionsListResult {
  /** Every loaded page, in `updated_at desc, token` order. */
  collections: Array<LibraryCollectionCard>
  isLoading: boolean
  isLoadingMore: boolean
  hasError: boolean
  hasMore: boolean
  loadMore: () => void
  refresh: () => Promise<unknown>
  /** POST, then mutate the list. */
  create: (body: CreateLibraryCollectionBody) => Promise<LibraryCollectionDetail>
}

/**
 * The viewer's collections over `GET /library/collections?cursor=` (50 per
 * page), with cursor paging. The Library home grid, the list page and the
 * add menu's targets read it. `enabled` false reads nothing.
 */
export function useLibraryCollectionsList(enabled = true): UseLibraryCollectionsListResult {
  const { data, error, isLoading, isValidating, size, setSize, mutate } =
    useSWRInfinite<LibraryCollectionCardsPage>(
      (pageIndex: number, previous: LibraryCollectionCardsPage | null) =>
        enabled ? libraryCollectionsListKey(pageIndex, previous) : null,
      (key: [string, string | null]) => libraryCollectionsApi.listCollections(key[1] ?? undefined),
      // `revalidateFirstPage` stays on (the default): a write elsewhere refetches the
      // list through `unstable_serialize`, which only reaches the first page, and an
      // edited collection moves to the top of it (`updated_at desc`).
      {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
      },
    )

  const pages = data ?? []
  const lastPage = pages.length > 0 ? pages[pages.length - 1] : null
  const hasMore = Boolean(lastPage?.nextCursor)
  const isLoadingMore = isValidating && size > pages.length

  const create = useCallback(
    async (body: CreateLibraryCollectionBody) => {
      const detail = await libraryCollectionsApi.createCollection(body)
      await mutate()
      return detail
    },
    [mutate],
  )

  return {
    collections: pages.flatMap((page) => page.items),
    isLoading: enabled && isLoading,
    isLoadingMore,
    hasError: Boolean(error),
    hasMore,
    loadMore: () => {
      if (hasMore && !isLoadingMore) {
        void setSize(size + 1)
      }
    },
    refresh: () => mutate(),
    create,
  }
}
